<script lang="ts">
import type { UnwrapRef} from 'vue';
import {
  defineComponent, reactive, ref
} from 'vue';
import { UserOutlined, LockOutlined } from '@ant-design/icons-vue';
import { object, string, func } from 'vue-types';
import type { RuleObject } from 'ant-design-vue/lib/form';
import {
  Input,
  Form,
  FormItem,
  Button,
  InputPassword,
  message,
  Spin,
} from 'ant-design-vue';

import type { FormValues, LoginFormResponse } from './types';

import ApiErrors from '@/ui/ApiErrors/index.vue';
import type { ServerErrors } from '@/util/api/ajax/ApiResponse';

export type { FormValues, LoginFormResponse };

export default defineComponent({
  name: 'LoginForm',
  components: {
    Input,
    InputPassword,
    Form,
    FormItem,
    Button,
    Spin,
    UserOutlined,
    LockOutlined,
    ApiErrors,
  },
  props: {
    class: string().def(''),
    initialValues: object<Partial<FormValues>>().def(undefined),
    onFinish: func<(values: FormValues) => void>().isRequired.def(undefined),
    error: object<ServerErrors>().def(undefined),
    loading: Boolean,
    inintialShowFormControls: Boolean,
  },
  setup(properties) {
    const formRef = ref();
    const showFormControls = ref(properties.inintialShowFormControls);
    const formValues: UnwrapRef<Partial<FormValues>> = reactive({
      ...properties.initialValues,
    });

    const rules: Record<string, RuleObject | RuleObject[]> = {
      email: [
        {
          required: true,
          message: 'Please input email',
          trigger: ['blur', 'change'],
        },
        {
          type: 'email',
          message: 'email is invalid',
          trigger: ['blur', 'change'],
        },
      ],
      password: [
        {
          required: true,
          message: 'Please input password',
          trigger: ['blur', 'change'],
        },
      ],
    };

    const info = () => {
      message.info('Please contact an administrator to reset your password.');
    };

    return {
      formRef,
      showFormControls,
      formValues,
      rules,
      onSubmit: () => formRef.value.validate(),
      info,
    };
  },
  methods: {
    isButtonDisabled() {
      if (this.loading) {
        return this.loading;
      }
      // return !this.formValues.email || !this.formValues.password;
      return false;
    },
  },
});
</script>

<template>
  <Form
    ref="formRef"
    class="mt-8"
    :model="formValues"
    :rules="rules"
    @finish="onFinish"
  >
    <ApiErrors v-if="error" :error="error" class="my-8" />
    <div v-if="!showFormControls" class="my-4">
      <Button
        html-type="button"
        class="w-full"
        size="large"
        type="primary"
        @click="showFormControls = true"
      >
        Sign in with Email
      </Button>
    </div>
    <template v-else>
      <Spin :spinning="!!loading">
        <FormItem name="email" class="my-8">
          <Input
            v-model:value="formValues.email"
            placeholder="Email"
            size="large"
          >
            <template #prefix>
              <UserOutlined style="color: rgba(0, 0, 0, 0.25)" />
            </template>
          </Input>
        </FormItem>
        <FormItem name="password" class="my-8">
          <InputPassword
            v-model:value="formValues.password"
            placeholder="Password"
            size="large"
            autocomplete="password"
          >
            <template #prefix>
              <LockOutlined style="color: rgba(0, 0, 0, 0.25)" />
            </template>
          </InputPassword>
        </FormItem>
      </Spin>
      <FormItem style="margin-bottom: 0">
        <Button
          :loading="loading"
          type="primary"
          html-type="submit"
          size="large"
          class="w-full"
          data-test-id="login"
          :disabled="isButtonDisabled()"
          @click="onSubmit"
        >
          Log in
        </Button>
      </FormItem>
      <FormItem>
        <div class="text-center mt-2">
          <Button
            type="link"
            style="padding: 0"
            @click="info"
          >
            Forgot password?
          </Button>
        </div>
      </FormItem>
    </template>
  </Form>
</template>
